/* Align neighbouring control rows with the logo, not with its date/menu stack. */
(() => {
  if (customElements.get('header-desktop-groups')) return;

  class HeaderDesktopGroups extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.desktop = matchMedia('(min-width: 768px)');
      this.lifecycle = new AbortController();
      this.desktop.addEventListener('change', () => this.schedule(), { signal: this.lifecycle.signal });
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this);
      this.querySelectorAll('.header-brand, .site-header__logo, .header-controls, .header-desktop-links')
        .forEach(element => this.resize.observe(element));
      // The existing alignment choice can change without changing any sizes.
      this.settings = new MutationObserver(() => this.schedule());
      [this.parentElement, this.closest('.titan-header')].filter(Boolean).forEach(element =>
        this.settings.observe(element, { attributes: true, attributeFilter: ['style', 'data-desktop-layout'] }));
      this.layout();
    }

    schedule() {
      if (!this.frame && this.isConnected) this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.layout();
      });
    }

    layout() {
      const zones = Array.from(this.children);
      const logo = this.querySelector('.site-header__logo');
      if (!this.desktop?.matches || !logo?.getBoundingClientRect().height) {
        this.removeAttribute('data-logo-aligned');
        zones.forEach(zone => zone.style.removeProperty('--header-zone-offset'));
        return;
      }
      this.setAttribute('data-logo-aligned', '');
      const logoZone = logo.closest('.header-zone');
      const alignment = getComputedStyle(this).getPropertyValue('--header-control-valign').trim();
      const fraction = alignment === 'start' ? 0 : alignment === 'end' ? 1 : .5;
      const offset = (element, zone) => {
        const rect = element.getBoundingClientRect();
        // Subtract our own padding so every measurement is intrinsic. Repeated
        // observer callbacks must not accumulate a correction or drift the row.
        return rect.top - zone.getBoundingClientRect().top
          - (parseFloat(getComputedStyle(zone).paddingTop) || 0) + rect.height * fraction;
      };
      const reference = offset(logo, logoZone);
      const shifts = zones.map(zone => {
        if (zone === logoZone) return 0;
        const row = zone.querySelector(':scope > .header-controls, :scope > .header-desktop-links');
        return row ? reference - offset(row, zone) : 0;
      });
      // A large badge or wrapped control can be taller than the logo. Advance
      // the other columns too, keeping every hit target inside normal layout.
      const earliest = Math.min(0, ...shifts);
      zones.forEach((zone, index) => {
        const value = `${Math.round((shifts[index] - earliest) * 1000) / 1000}px`;
        if (zone.style.getPropertyValue('--header-zone-offset') !== value)
          zone.style.setProperty('--header-zone-offset', value);
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.resize?.disconnect();
      this.settings?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }
  customElements.define('header-desktop-groups', HeaderDesktopGroups);
})();
